import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SplitButton } from 'primereact/splitbutton';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import loadGoogleMaps from '../../utils/loadGoogleMaps';
import CityGeoInstructionsDialog, {
  hasInstructions,
  instructionsStepCount,
} from './CityGeoInstructionsDialog';

const DEFAULT_CENTER = { lat: 32.5149, lng: -117.0382 };
const DEFAULT_ZOOM = 14;
const MAP_HEIGHT = 546;
const MAP_LOADING_HEIGHT = 468;

const GEO_STROKE = '#2020A0';
const GEO_FILL = '#A5B4FC';
const QUEUE_STROKE = '#38BDF8';

const POLYGON_OPTIONS = {
  fillColor: GEO_FILL,
  fillOpacity: 0.22,
  strokeColor: GEO_STROKE,
  strokeWeight: 2,
  editable: false,
  draggable: false,
  clickable: false,
};

const CROSSING_POLYGON_OPTIONS = {
  fillColor: '#86EFAC',
  fillOpacity: 0.22,
  strokeColor: '#15803D',
  strokeWeight: 2,
  editable: false,
  draggable: false,
  clickable: false,
};

const QUEUE_OPTIONS = {
  strokeColor: QUEUE_STROKE,
  strokeOpacity: 0.95,
  strokeWeight: 6,
  geodesic: true,
  editable: false,
  draggable: false,
  clickable: false,
};

/** Direction arrows along the queue (path runs entrance → garita). */
function queueLineOptions(extra = {}) {
  const g = window.google?.maps;
  const base = { ...QUEUE_OPTIONS, zIndex: 5, ...extra };
  if (!g?.SymbolPath) return base;
  return {
    ...base,
    icons: [
      {
        icon: {
          path: g.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 3.4,
          strokeColor: '#0369A1',
          strokeWeight: 1.25,
          fillColor: '#FFFFFF',
          fillOpacity: 1,
        },
        offset: '20px',
        repeat: '42px',
      },
    ],
  };
}

const RADIUS_OPTIONS = {
  fillColor: GEO_FILL,
  fillOpacity: 0.14,
  strokeColor: GEO_STROKE,
  strokeWeight: 2,
};

const HIDE_POI_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.man_made', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

function coordsFromGeometry(geometry) {
  if (!geometry?.coordinates?.[0]) return [];
  const ring = geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) ring.pop();
  }
  return ring;
}

function coordsFromQueuePath(queuePath) {
  if (!queuePath?.coordinates?.length) return [];
  return queuePath.coordinates
    .map((pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return null;
      return { lat: Number(pair[1]), lng: Number(pair[0]) };
    })
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
}

function queuePathFromLatLngs(points) {
  if (!points?.length || points.length < 2) return null;
  return {
    type: 'LineString',
    coordinates: points.map((p) => [Number(p.lng), Number(p.lat)]),
  };
}

function svgMarkerIcon(svg, size) {
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
}

function circleSvg({ size, fill, stroke, strokeWidth = 3, plus = false }) {
  const mid = size / 2;
  const r = mid - strokeWidth;
  const plusLen = Math.max(4, r * 0.7);
  const plusPath = plus
    ? `<path d="M${mid} ${mid - plusLen} v${plusLen * 2} M${mid - plusLen} ${mid} h${plusLen * 2}" stroke="${stroke}" stroke-width="2" stroke-linecap="round"/>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${mid}" cy="${mid}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
    ${plusPath}
  </svg>`;
}

function vertexIcon() {
  return svgMarkerIcon(circleSvg({ size: 20, fill: '#FFFFFF', stroke: GEO_STROKE }), 20);
}

function midpointIcon() {
  return svgMarkerIcon(
    circleSvg({ size: 18, fill: '#EEF2FF', stroke: '#2020A0', strokeWidth: 2, plus: true }),
    18
  );
}

function drawingVertexIcon(isFirst = false, canClose = false) {
  if (isFirst && canClose) {
    return svgMarkerIcon(circleSvg({ size: 22, fill: GEO_STROKE, stroke: '#FFFFFF' }), 22);
  }
  if (isFirst) {
    return svgMarkerIcon(circleSvg({ size: 20, fill: '#FFFFFF', stroke: GEO_STROKE }), 20);
  }
  return svgMarkerIcon(circleSvg({ size: 18, fill: '#FFFFFF', stroke: '#2020A0' }), 18);
}

function queueVertexIcon() {
  return svgMarkerIcon(circleSvg({ size: 16, fill: '#FFFFFF', stroke: QUEUE_STROKE }), 16);
}

function edgeMidpoint(a, b) {
  const pa = toLatLngLiteral(a);
  const pb = toLatLngLiteral(b);
  if (!pa || !pb) return null;
  return { lat: (pa.lat + pb.lat) / 2, lng: (pa.lng + pb.lng) / 2 };
}

function toLatLngLiteral(value) {
  if (!value) return null;
  if (typeof value.lat === 'function') return { lat: value.lat(), lng: value.lng() };
  return { lat: Number(value.lat), lng: Number(value.lng) };
}

function isNearLatLng(map, a, b, px = 20) {
  const pa = toLatLngLiteral(a);
  const pb = toLatLngLiteral(b);
  if (!map || !pa || !pb) return false;
  const bounds = map.getBounds();
  const div = map.getDiv();
  if (!bounds || !div) return false;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const width = div.offsetWidth || 1;
  const height = div.offsetHeight || 1;
  const lngSpan = ne.lng() - sw.lng() || 1;
  const latSpan = ne.lat() - sw.lat() || 1;
  const dx = ((pa.lng - pb.lng) / lngSpan) * width;
  const dy = ((pa.lat - pb.lat) / latSpan) * height;
  return Math.hypot(dx, dy) <= px;
}

function photonLabel(feat) {
  const p = feat?.properties || {};
  return [p.name, p.city, p.state, p.country]
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index)
    .join(', ');
}

function photonBounds(extent) {
  if (!extent?.length || extent.length < 4 || !window.google?.maps?.LatLngBounds) return null;
  const [a, b, c, d] = extent.map(Number);
  return new window.google.maps.LatLngBounds(
    { lat: Math.min(b, d), lng: Math.min(a, c) },
    { lat: Math.max(b, d), lng: Math.max(a, c) }
  );
}

function photonToSuggestion(feat, index) {
  const coords = feat?.geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const [lng, lat] = coords;
  return {
    id: `${feat.properties?.osm_id || index}-${lng}-${lat}`,
    label: photonLabel(feat) || 'Unknown place',
    lat: Number(lat),
    lng: Number(lng),
    bounds: photonBounds(feat.properties?.extent),
  };
}

/**
 * Combined polygon + queue polyline + garita pin picker.
 * onChange({ geometry, queuePath, lat, lng, arrivalRadiusMeters, instructions })
 *
 * pinMode:
 *  - edit: place/drag pin (default)
 *  - locked: show shared pin, no edit
 *  - off: hide pin UI
 * showGeofence / showQueue: toggle route-layer editors
 * showInstructions: Add/Edit instructions button (default: when route layers on)
 */
export default function CityGeoMapPicker({
  queueGeofence,
  geometry,
  queuePath,
  lat,
  lng,
  crossingRadiusMeters,
  arrivalRadiusMeters = 150,
  crossingZoneMode = 'radius',
  crossingGeofence,
  instructions = null,
  onChange,
  center,
  zoom,
  pinMode = 'edit',
  showGeofence = true,
  showQueue = true,
  showInstructions,
  instructionsLabel = 'this route',
  showArrivalRadius,
  showCrossingZone,
}) {
  const queuePolygon = queueGeofence || geometry;
  const radiusMeters = crossingRadiusMeters ?? arrivalRadiusMeters ?? 150;
  const pinEditable = pinMode === 'edit';
  const pinVisible = pinMode !== 'off';
  const instructionsEnabled =
    showInstructions != null ? !!showInstructions : showGeofence || showQueue;
  const crossingZoneEnabled =
    showCrossingZone != null ? !!showCrossingZone : showGeofence || showQueue;
  const [zoneMode, setZoneMode] = useState(
    crossingZoneMode === 'geofence' || crossingZoneMode === 'polygon' ? 'geofence' : 'radius'
  );
  const arrivalRadiusEnabled =
    showArrivalRadius != null
      ? !!showArrivalRadius
      : crossingZoneEnabled && zoneMode === 'radius';
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonRef = useRef(null);
  const crossingPolygonRef = useRef(null);
  const polygonLayerRef = useRef('queue');
  const queuePolylineRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const previewLineRef = useRef(null);
  const drawingPathRef = useRef([]);
  const queueDrawingPathRef = useRef([]);
  const drawingMarkersRef = useRef([]);
  const drawingMidpointMarkersRef = useRef([]);
  const queueDrawingMarkersRef = useRef([]);
  const vertexMarkersRef = useRef([]);
  const midpointMarkersRef = useRef([]);
  const syncDrawingHandlesRef = useRef(() => {});
  const polygonListenersRef = useRef([]);
  const isDrawingRef = useRef(false);
  const isDrawingQueueRef = useRef(false);
  const isPlacingPinRef = useRef(false);
  const isEditingGeofenceRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const stateRef = useRef({ geometry, queuePath, lat, lng, arrivalRadiusMeters });
  const hidePoisRef = useRef(true);
  const radiusRef = useRef(arrivalRadiusMeters || 150);
  const finishDrawingRef = useRef(() => {});
  const removeDrawingVertexRef = useRef(() => {});
  const finishQueueDrawingRef = useRef(() => {});
  const handleDrawClickRef = useRef(() => {});
  const handleQueueDrawClickRef = useRef(() => {});
  const lastDrawTsRef = useRef(0);
  const searchBoxRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const suppressSearchRef = useRef(false);

  const [loaded, setLoaded] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDrawingQueue, setIsDrawingQueue] = useState(false);
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [isEditingGeofence, setIsEditingGeofence] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);
  const [queueVertexCount, setQueueVertexCount] = useState(0);
  const [radius, setRadius] = useState(radiusMeters || 150);
  const [hidePois, setHidePois] = useState(true);
  const [hasPin, setHasPin] = useState(lat != null && lng != null);
  const [hasPolygon, setHasPolygon] = useState(!!queuePolygon?.coordinates?.[0]);
  const [hasCrossingPolygon, setHasCrossingPolygon] = useState(
    !!crossingGeofence?.coordinates?.[0]
  );
  const [hasQueue, setHasQueue] = useState(!!queuePath?.coordinates?.length);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [searching, setSearching] = useState(false);

  onChangeRef.current = onChange;
  hidePoisRef.current = hidePois;
  radiusRef.current = radius;

  // Sync from props after commit — never during render, or a mid-draw emit
  // gets wiped by stale geometry/queuePath/pin from the previous parent state.
  useEffect(() => {
    stateRef.current = {
      geometry: queuePolygon,
      queueGeofence: queuePolygon,
      queuePath,
      lat,
      lng,
      crossingRadiusMeters: radiusMeters,
      crossingZoneMode: zoneMode,
      crossingGeofence,
    };
  }, [queuePolygon, queuePath, lat, lng, radiusMeters, zoneMode, crossingGeofence]);

  const emit = useCallback((patch) => {
    const next = { ...patch };
    if ('geometry' in next && !('queueGeofence' in next)) {
      next.queueGeofence = next.geometry;
    }
    if ('queueGeofence' in next && !('geometry' in next)) {
      next.geometry = next.queueGeofence;
    }
    if ('crossingRadiusMeters' in next && !('arrivalRadiusMeters' in next)) {
      next.arrivalRadiusMeters = next.crossingRadiusMeters;
    }
    if ('arrivalRadiusMeters' in next && !('crossingRadiusMeters' in next)) {
      next.crossingRadiusMeters = next.arrivalRadiusMeters;
    }
    stateRef.current = {
      ...stateRef.current,
      crossingRadiusMeters: radiusRef.current,
      ...next,
    };
    onChangeRef.current?.(next);
  }, []);

  const applyPlace = useCallback((place) => {
    const map = mapInstanceRef.current;
    if (!map || !place) return;
    if (place.bounds) map.fitBounds(place.bounds);
    else {
      map.panTo({ lat: place.lat, lng: place.lng });
      map.setZoom(12);
    }
    suppressSearchRef.current = true;
    setSearchQuery(place.label);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightIndex(0);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6`
      );
      if (!res.ok) throw new Error('search failed');
      const data = await res.json();
      const next = (data?.features || []).map(photonToSuggestion).filter(Boolean);
      setSuggestions(next);
      setHighlightIndex(0);
      setShowSuggestions(true);
    } catch (err) {
      console.error(err);
      setSuggestions([]);
      setShowSuggestions(true);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    window.clearTimeout(searchDebounceRef.current);
    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      return undefined;
    }
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearching(false);
      return undefined;
    }
    searchDebounceRef.current = window.setTimeout(() => {
      fetchSuggestions(q);
    }, 250);
    return () => window.clearTimeout(searchDebounceRef.current);
  }, [searchQuery, fetchSuggestions]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const getPolygonCoords = useCallback((polygon) => {
    const path = polygon.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push([point.lng(), point.lat()]);
    }
    if (coords.length > 0) coords.push(coords[0]);
    return coords;
  }, []);

  const emitPolygon = useCallback((polygon) => {
    const shape = { type: 'Polygon', coordinates: [getPolygonCoords(polygon)] };
    if (polygonLayerRef.current === 'crossing') {
      emit({ crossingGeofence: shape });
      return;
    }
    emit({ queueGeofence: shape, geometry: shape });
  }, [emit, getPolygonCoords]);

  const clearMarkerList = (listRef) => {
    listRef.current.forEach((marker) => marker.setMap(null));
    listRef.current = [];
  };

  const clearPolygonListeners = useCallback(() => {
    polygonListenersRef.current.forEach((listener) => listener.remove());
    polygonListenersRef.current = [];
  }, []);

  const syncPolygonHandlesRef = useRef(() => {});

  const removePolygonVertex = useCallback((polygon, index) => {
    const path = polygon.getPath();
    if (path.getLength() <= 3) return;
    path.removeAt(index);
    emitPolygon(polygon);
    syncPolygonHandlesRef.current?.(polygon);
  }, [emitPolygon]);

  const syncPolygonHandles = useCallback((polygon) => {
    clearMarkerList(vertexMarkersRef);
    clearMarkerList(midpointMarkersRef);
    if (!polygon || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const path = polygon.getPath();
    const length = path.getLength();
    const canDelete = length > 3;

    for (let i = 0; i < length; i += 1) {
      const index = i;
      const marker = new window.google.maps.Marker({
        map,
        position: path.getAt(index),
        draggable: true,
        icon: vertexIcon(),
        zIndex: 1300,
        cursor: 'grab',
        crossOnDrag: false,
        optimized: false,
        title: canDelete
          ? 'Drag to move · double-click to delete'
          : 'Drag to move (keep at least 3 points)',
      });
      marker.addListener('dragstart', () => {
        clearMarkerList(midpointMarkersRef);
      });
      marker.addListener('drag', () => {
        const pos = marker.getPosition();
        if (pos) path.setAt(index, pos);
      });
      marker.addListener('dragend', () => {
        emitPolygon(polygon);
        syncPolygonHandles(polygon);
      });
      marker.addListener('dblclick', (event) => {
        event?.stop?.();
        removePolygonVertex(polygon, index);
      });
      marker.addListener('rightclick', () => {
        removePolygonVertex(polygon, index);
      });
      vertexMarkersRef.current.push(marker);
    }

    for (let i = 0; i < length; i += 1) {
      const insertAt = i + 1;
      const mid = edgeMidpoint(path.getAt(i), path.getAt((i + 1) % length));
      if (!mid) continue;
      let insertedIndex = null;
      const marker = new window.google.maps.Marker({
        map,
        position: mid,
        draggable: true,
        icon: midpointIcon(),
        zIndex: 1250,
        cursor: 'grab',
        crossOnDrag: false,
        optimized: false,
        title: 'Drag the + to add a point on this edge',
      });
      marker.addListener('dragstart', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        path.insertAt(insertAt, pos);
        insertedIndex = insertAt;
        marker.setIcon(vertexIcon());
        marker.setZIndex(1400);
        vertexMarkersRef.current.forEach((vertex) => vertex.setOpacity(0.35));
      });
      marker.addListener('drag', () => {
        const pos = marker.getPosition();
        if (insertedIndex == null || !pos) return;
        path.setAt(insertedIndex, pos);
      });
      marker.addListener('dragend', () => {
        emitPolygon(polygon);
        syncPolygonHandles(polygon);
      });
      marker.addListener('click', () => {
        if (insertedIndex != null) return;
        path.insertAt(insertAt, marker.getPosition());
        emitPolygon(polygon);
        syncPolygonHandles(polygon);
      });
      midpointMarkersRef.current.push(marker);
    }
  }, [emitPolygon, removePolygonVertex]);
  syncPolygonHandlesRef.current = syncPolygonHandles;

  const applyPolygon = useCallback((polygon) => {
    polygonLayerRef.current = 'queue';
    clearPolygonListeners();
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    polygonRef.current = polygon;
    polygonListenersRef.current.push(
      polygon.getPath().addListener('set_at', () => emitPolygon(polygon))
    );
    setHasPolygon(true);
    emitPolygon(polygon);
  }, [clearPolygonListeners, emitPolygon]);

  const placePin = useCallback((position, nextRadius = radiusRef.current) => {
    if (!mapInstanceRef.current || !position) return;
    if (pinMode === 'off') {
      markerRef.current?.setMap(null);
      circleRef.current?.setMap(null);
      setHasPin(false);
      return;
    }
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map: mapInstanceRef.current,
        position,
        draggable: pinMode === 'edit',
        title: pinMode === 'edit' ? 'Garita — drag to edit' : 'Shared garita destination',
        zIndex: 1300,
      });
      markerRef.current.addListener('dragend', () => {
        if (pinMode !== 'edit') return;
        const pos = markerRef.current.getPosition();
        if (!pos) return;
        circleRef.current?.setCenter(pos);
        emit({ lat: pos.lat(), lng: pos.lng() });
      });
      markerRef.current.addListener('click', (event) => {
        if (!isDrawingRef.current) return;
        handleDrawClickRef.current?.(event.latLng || markerRef.current.getPosition());
      });
    } else {
      markerRef.current.setMap(mapInstanceRef.current);
      markerRef.current.setPosition(position);
      markerRef.current.setDraggable(pinMode === 'edit');
      markerRef.current.setTitle(
        pinMode === 'edit' ? 'Garita — drag to edit' : 'Shared garita destination'
      );
    }

    if (!arrivalRadiusEnabled) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      setHasPin(true);
      return;
    }

    if (!circleRef.current) {
      circleRef.current = new window.google.maps.Circle({
        map: mapInstanceRef.current,
        center: position,
        radius: nextRadius,
        fillColor: RADIUS_OPTIONS.fillColor,
        fillOpacity: RADIUS_OPTIONS.fillOpacity,
        strokeColor: RADIUS_OPTIONS.strokeColor,
        strokeWeight: RADIUS_OPTIONS.strokeWeight,
        clickable: false,
        zIndex: 1,
      });
      circleRef.current.addListener('click', (event) => {
        if (isDrawingRef.current && event.latLng) handleDrawClickRef.current?.(event.latLng);
      });
    } else {
      circleRef.current.setMap(mapInstanceRef.current);
      circleRef.current.setCenter(position);
      circleRef.current.setRadius(nextRadius);
    }
    setHasPin(true);
  }, [arrivalRadiusEnabled, emit, pinMode]);

  const clearPreview = useCallback(() => {
    if (previewLineRef.current) {
      previewLineRef.current.setMap(null);
      previewLineRef.current = null;
    }
    clearMarkerList(drawingMarkersRef);
    clearMarkerList(drawingMidpointMarkersRef);
    drawingPathRef.current = [];
    setVertexCount(0);
  }, []);

  const hidePolygonHandles = useCallback(() => {
    clearMarkerList(vertexMarkersRef);
    clearMarkerList(midpointMarkersRef);
  }, []);

  const setOverlayPickMode = useCallback((drawing) => {
    circleRef.current?.setOptions({ clickable: !!drawing, zIndex: 1 });
    if (markerRef.current) {
      markerRef.current.setDraggable(
        !drawing && !isPlacingPinRef.current && pinMode === 'edit'
      );
      markerRef.current.setClickable(true);
      markerRef.current.setZIndex(drawing ? 400 : 1300);
    }
  }, [pinMode]);

  const setEditingGeofenceMode = useCallback((enabled) => {
    isEditingGeofenceRef.current = enabled;
    setIsEditingGeofence(enabled);
    if (enabled) {
      isPlacingPinRef.current = false;
      setIsPlacingPin(false);
      isDrawingRef.current = false;
      setIsDrawing(false);
      clearPreview();
      polygonRef.current?.setOptions({
        fillOpacity: POLYGON_OPTIONS.fillOpacity,
        strokeOpacity: 1,
        clickable: false,
      });
      if (polygonRef.current) syncPolygonHandles(polygonRef.current);
      setOverlayPickMode(false);
      mapInstanceRef.current?.setOptions({
        disableDoubleClickZoom: true,
        draggableCursor: null,
      });
      return;
    }
    hidePolygonHandles();
    mapInstanceRef.current?.setOptions({
      disableDoubleClickZoom: false,
      draggableCursor: null,
    });
  }, [clearPreview, hidePolygonHandles, setOverlayPickMode, syncPolygonHandles]);

  const setDrawingMode = useCallback((enabled) => {
    isDrawingRef.current = enabled;
    setIsDrawing(enabled);
    if (enabled) {
      isPlacingPinRef.current = false;
      setIsPlacingPin(false);
      isDrawingQueueRef.current = false;
      setIsDrawingQueue(false);
      isEditingGeofenceRef.current = false;
      setIsEditingGeofence(false);
      hidePolygonHandles();
      polygonRef.current?.setOptions({ fillOpacity: 0.08, strokeOpacity: 0.35, clickable: false });
    } else {
      polygonRef.current?.setOptions({ fillOpacity: POLYGON_OPTIONS.fillOpacity, strokeOpacity: 1, clickable: false });
      if (isEditingGeofenceRef.current && polygonRef.current) {
        syncPolygonHandles(polygonRef.current);
      } else {
        hidePolygonHandles();
      }
    }
    setOverlayPickMode(enabled);
    mapInstanceRef.current?.setOptions({
      disableDoubleClickZoom: enabled,
      draggableCursor: enabled || isPlacingPinRef.current || isDrawingQueueRef.current ? 'crosshair' : null,
    });
  }, [hidePolygonHandles, setOverlayPickMode, syncPolygonHandles]);

  const setPlacingPinMode = useCallback((enabled) => {
    if (enabled && pinMode !== 'edit') return;
    isPlacingPinRef.current = enabled;
    setIsPlacingPin(enabled);
    if (enabled) {
      isDrawingRef.current = false;
      setIsDrawing(false);
      isDrawingQueueRef.current = false;
      setIsDrawingQueue(false);
      isEditingGeofenceRef.current = false;
      setIsEditingGeofence(false);
      clearPreview();
      hidePolygonHandles();
      polygonRef.current?.setOptions({
        fillOpacity: POLYGON_OPTIONS.fillOpacity,
        strokeOpacity: 1,
        clickable: false,
      });
      setOverlayPickMode(false);
    }
    mapInstanceRef.current?.setOptions({
      disableDoubleClickZoom: false,
      draggableCursor: enabled ? 'crosshair' : null,
    });
  }, [clearPreview, hidePolygonHandles, setOverlayPickMode]);

  const clearQueuePreview = useCallback(() => {
    clearMarkerList(queueDrawingMarkersRef);
    queueDrawingPathRef.current = [];
    setQueueVertexCount(0);
  }, []);

  const applyQueuePath = useCallback((points) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (queuePolylineRef.current) {
      queuePolylineRef.current.setMap(null);
      queuePolylineRef.current = null;
    }
    if (!points || points.length < 2) {
      setHasQueue(false);
      emit({ queuePath: null });
      return;
    }
    queuePolylineRef.current = new window.google.maps.Polyline({
      ...queueLineOptions(),
      map,
      path: points,
    });
    setHasQueue(true);
    emit({ queuePath: queuePathFromLatLngs(points) });
  }, [emit]);

  const reverseQueuePath = useCallback(() => {
    if (isDrawingQueueRef.current) return;
    const coords = coordsFromQueuePath(stateRef.current.queuePath);
    if (coords.length < 2) return;
    applyQueuePath([...coords].reverse());
  }, [applyQueuePath]);

  const setDrawingQueueMode = useCallback((enabled) => {
    isDrawingQueueRef.current = enabled;
    setIsDrawingQueue(enabled);
    if (enabled) {
      isDrawingRef.current = false;
      setIsDrawing(false);
      isPlacingPinRef.current = false;
      setIsPlacingPin(false);
      isEditingGeofenceRef.current = false;
      setIsEditingGeofence(false);
      clearPreview();
      hidePolygonHandles();
      if (queuePolylineRef.current) {
        queuePolylineRef.current.setMap(null);
        queuePolylineRef.current = null;
      }
      queueDrawingPathRef.current = [];
      setQueueVertexCount(0);
      setOverlayPickMode(false);
    }
    mapInstanceRef.current?.setOptions({
      disableDoubleClickZoom: enabled,
      draggableCursor: enabled ? 'crosshair' : null,
    });
  }, [clearPreview, hidePolygonHandles, setOverlayPickMode]);

  const refreshDrawingPreview = useCallback(() => {
    const map = mapInstanceRef.current;
    const path = drawingPathRef.current;
    if (!map) return;
    if (!path.length) {
      if (previewLineRef.current) {
        previewLineRef.current.setMap(null);
        previewLineRef.current = null;
      }
      return;
    }
    if (!previewLineRef.current) {
      previewLineRef.current = new window.google.maps.Polyline({
        map,
        path,
        strokeColor: GEO_STROKE,
        strokeWeight: 2,
        strokeOpacity: 0.95,
        clickable: false,
        zIndex: 2,
      });
    } else {
      previewLineRef.current.setPath(path);
    }
  }, []);

  const syncDrawingHandles = useCallback(() => {
    clearMarkerList(drawingMarkersRef);
    clearMarkerList(drawingMidpointMarkersRef);
    const map = mapInstanceRef.current;
    const path = drawingPathRef.current;
    if (!map || !path.length) return;

    const canClose = path.length >= 3;
    path.forEach((latLng, index) => {
      const isFirst = index === 0;
      const marker = new window.google.maps.Marker({
        map,
        position: latLng,
        draggable: true,
        icon: drawingVertexIcon(isFirst, isFirst && canClose),
        zIndex: 2000,
        cursor: isFirst && canClose ? 'pointer' : 'grab',
        crossOnDrag: false,
        optimized: false,
        title: isFirst
          ? (canClose
            ? 'Click to close the geofence · drag to move · double-click to delete'
            : 'Start point — drag to move · double-click to delete')
          : 'Drag to move · double-click to delete',
      });
      marker.addListener('dragstart', () => {
        clearMarkerList(drawingMidpointMarkersRef);
      });
      marker.addListener('drag', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        drawingPathRef.current[index] = pos;
        previewLineRef.current?.setPath(drawingPathRef.current);
      });
      marker.addListener('dragend', () => {
        syncDrawingHandlesRef.current();
      });
      if (isFirst) {
        let closeTimer = null;
        marker.addListener('click', () => {
          if (drawingPathRef.current.length < 3) return;
          window.clearTimeout(closeTimer);
          closeTimer = window.setTimeout(() => finishDrawingRef.current(), 250);
        });
        marker.addListener('dblclick', (event) => {
          event?.stop?.();
          window.clearTimeout(closeTimer);
          removeDrawingVertexRef.current(index);
        });
      } else {
        marker.addListener('dblclick', (event) => {
          event?.stop?.();
          removeDrawingVertexRef.current(index);
        });
      }
      marker.addListener('rightclick', () => {
        removeDrawingVertexRef.current(index);
      });
      drawingMarkersRef.current.push(marker);
    });

    for (let i = 0; i < path.length - 1; i += 1) {
      const insertAt = i + 1;
      const mid = edgeMidpoint(path[i], path[i + 1]);
      if (!mid) continue;
      let insertedIndex = null;
      const marker = new window.google.maps.Marker({
        map,
        position: mid,
        draggable: true,
        icon: midpointIcon(),
        zIndex: 1900,
        cursor: 'grab',
        crossOnDrag: false,
        optimized: false,
        title: 'Drag the + to add a point on this edge',
      });
      marker.addListener('dragstart', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        drawingPathRef.current.splice(insertAt, 0, pos);
        insertedIndex = insertAt;
        setVertexCount(drawingPathRef.current.length);
        marker.setIcon(drawingVertexIcon(false, false));
        marker.setZIndex(2100);
        drawingMarkersRef.current.forEach((vertex) => vertex.setOpacity(0.35));
        refreshDrawingPreview();
      });
      marker.addListener('drag', () => {
        const pos = marker.getPosition();
        if (insertedIndex == null || !pos) return;
        drawingPathRef.current[insertedIndex] = pos;
        previewLineRef.current?.setPath(drawingPathRef.current);
      });
      marker.addListener('dragend', () => {
        syncDrawingHandlesRef.current();
      });
      marker.addListener('click', () => {
        if (insertedIndex != null) return;
        drawingPathRef.current.splice(insertAt, 0, marker.getPosition());
        setVertexCount(drawingPathRef.current.length);
        refreshDrawingPreview();
        syncDrawingHandlesRef.current();
      });
      drawingMidpointMarkersRef.current.push(marker);
    }
  }, [refreshDrawingPreview]);

  const removeDrawingVertex = useCallback((index) => {
    const path = drawingPathRef.current;
    if (index < 0 || index >= path.length) return;
    path.splice(index, 1);
    drawingPathRef.current = path;
    setVertexCount(path.length);
    refreshDrawingPreview();
    syncDrawingHandles();
  }, [refreshDrawingPreview, syncDrawingHandles]);

  removeDrawingVertexRef.current = removeDrawingVertex;
  syncDrawingHandlesRef.current = syncDrawingHandles;

  const addDrawingVertex = useCallback((latLng) => {
    const map = mapInstanceRef.current;
    if (!map || !latLng) return;
    drawingPathRef.current = [...drawingPathRef.current, latLng];
    setVertexCount(drawingPathRef.current.length);
    refreshDrawingPreview();
    syncDrawingHandles();
  }, [refreshDrawingPreview, syncDrawingHandles]);

  const undoLastDrawingVertex = useCallback(() => {
    removeDrawingVertex(drawingPathRef.current.length - 1);
  }, [removeDrawingVertex]);

  const finishDrawing = useCallback(() => {
    const path = drawingPathRef.current;
    if (path.length < 3 || !mapInstanceRef.current) return;
    const isCrossing = polygonLayerRef.current === 'crossing';
    const polygon = new window.google.maps.Polygon({
      ...(isCrossing ? CROSSING_POLYGON_OPTIONS : POLYGON_OPTIONS),
      paths: path,
      map: mapInstanceRef.current,
    });
    if (isCrossing) {
      if (crossingPolygonRef.current) crossingPolygonRef.current.setMap(null);
      crossingPolygonRef.current = polygon;
      setHasCrossingPolygon(true);
      emitPolygon(polygon);
    } else {
      applyPolygon(polygon);
      setEditingGeofenceMode(true);
    }
    clearPreview();
    setDrawingMode(false);
  }, [applyPolygon, clearPreview, emitPolygon, setDrawingMode, setEditingGeofenceMode]);

  const handleDrawClick = useCallback((latLng) => {
    if (!isDrawingRef.current || !latLng) return;
    const now = Date.now();
    if (now - lastDrawTsRef.current < 80) return;
    lastDrawTsRef.current = now;
    const path = drawingPathRef.current;
    if (path.length >= 3 && isNearLatLng(mapInstanceRef.current, latLng, path[0], 22)) {
      finishDrawing();
      return;
    }
    addDrawingVertex(latLng);
  }, [addDrawingVertex, finishDrawing]);

  finishDrawingRef.current = finishDrawing;
  handleDrawClickRef.current = handleDrawClick;

  const finishQueueDrawing = useCallback(() => {
    if (queueDrawingPathRef.current.length < 2) return;
    const points = queueDrawingPathRef.current.map((p) => {
      const lit = toLatLngLiteral(p);
      return { lat: lit.lat, lng: lit.lng };
    });
    applyQueuePath(points);
    clearQueuePreview();
    setDrawingQueueMode(false);
  }, [applyQueuePath, clearQueuePreview, setDrawingQueueMode]);

  const addQueueVertex = useCallback((latLng) => {
    const map = mapInstanceRef.current;
    if (!map || !latLng) return;
    queueDrawingPathRef.current = [...queueDrawingPathRef.current, latLng];
    const count = queueDrawingPathRef.current.length;
    setQueueVertexCount(count);

    const marker = new window.google.maps.Marker({
      map,
      position: latLng,
      icon: queueVertexIcon(),
      zIndex: 2000,
      clickable: false,
    });
    queueDrawingMarkersRef.current.push(marker);

    if (!queuePolylineRef.current) {
      queuePolylineRef.current = new window.google.maps.Polyline({
        ...queueLineOptions(),
        map,
        path: queueDrawingPathRef.current,
      });
    } else {
      queuePolylineRef.current.setPath(queueDrawingPathRef.current);
    }
  }, []);

  const handleQueueDrawClick = useCallback((latLng) => {
    if (!isDrawingQueueRef.current || !latLng) return;
    const now = Date.now();
    if (now - lastDrawTsRef.current < 80) return;
    lastDrawTsRef.current = now;
    addQueueVertex(latLng);
  }, [addQueueVertex]);

  finishQueueDrawingRef.current = finishQueueDrawing;
  handleQueueDrawClickRef.current = handleQueueDrawClick;

  const cancelDrawing = useCallback(() => {
    clearPreview();
    setDrawingMode(false);
  }, [clearPreview, setDrawingMode]);

  const cancelQueueDrawing = useCallback(() => {
    clearQueuePreview();
    if (queuePolylineRef.current) {
      queuePolylineRef.current.setMap(null);
      queuePolylineRef.current = null;
    }
    const existing = coordsFromQueuePath(stateRef.current.queuePath);
    if (existing.length >= 2) applyQueuePath(existing);
    setDrawingQueueMode(false);
  }, [applyQueuePath, clearQueuePreview, setDrawingQueueMode]);

  const clearGeofence = useCallback(() => {
    cancelDrawing();
    clearPolygonListeners();
    clearMarkerList(vertexMarkersRef);
    clearMarkerList(midpointMarkersRef);
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setHasPolygon(false);
    isEditingGeofenceRef.current = false;
    setIsEditingGeofence(false);
    emit({ queueGeofence: null, geometry: null });
  }, [cancelDrawing, clearPolygonListeners, emit]);

  const clearCrossingGeofence = useCallback(() => {
    if (crossingPolygonRef.current) {
      crossingPolygonRef.current.setMap(null);
      crossingPolygonRef.current = null;
    }
    setHasCrossingPolygon(false);
    emit({ crossingGeofence: null });
  }, [emit]);

  const clearQueue = useCallback(() => {
    cancelQueueDrawing();
    if (queuePolylineRef.current) {
      queuePolylineRef.current.setMap(null);
      queuePolylineRef.current = null;
    }
    setHasQueue(false);
    emit({ queuePath: null });
  }, [cancelQueueDrawing, emit]);

  const clearPin = useCallback(() => {
    setPlacingPinMode(false);
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    setHasPin(false);
    emit({ lat: null, lng: null });
  }, [emit, setPlacingPinMode]);

  useEffect(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!loaded || !mapRef.current) return;

    const pinCenter =
      lat != null && lng != null ? { lat: Number(lat), lng: Number(lng) } : null;
    const polyCoords = showGeofence ? coordsFromGeometry(queuePolygon) : [];
    const crossingCoords = crossingZoneEnabled ? coordsFromGeometry(crossingGeofence) : [];
    const queueCoords = showQueue ? coordsFromQueuePath(queuePath) : [];
    const mapCenter = pinCenter || center || polyCoords[0] || queueCoords[0] || DEFAULT_CENTER;

    const mapOptions = {
      center: mapCenter,
      zoom: zoom || DEFAULT_ZOOM,
      mapTypeId: mapType,
      styles: hidePoisRef.current ? HIDE_POI_STYLES : [],
      clickableIcons: !hidePoisRef.current,
      streetViewControl: false,
      mapTypeControl: false,
      // Fullscreen reparents the map DOM and often leaves a huge blank scroll area.
      fullscreenControl: false,
    };
    if (window.google.maps.RenderingType) {
      mapOptions.renderingType = window.google.maps.RenderingType.RASTER;
    }
    const map = new window.google.maps.Map(mapRef.current, mapOptions);
    mapInstanceRef.current = map;

    const frameEl = mapRef.current.parentElement;
    const lockCanvasSize = () => {
      if (!mapRef.current || !frameEl) return;
      const h = Math.round(frameEl.getBoundingClientRect().height) || MAP_HEIGHT;
      const w = Math.round(frameEl.getBoundingClientRect().width);
      mapRef.current.style.position = 'absolute';
      mapRef.current.style.top = '0';
      mapRef.current.style.left = '0';
      mapRef.current.style.right = '0';
      mapRef.current.style.bottom = '0';
      mapRef.current.style.width = w > 0 ? `${w}px` : '100%';
      mapRef.current.style.height = `${h}px`;
      mapRef.current.style.maxHeight = `${h}px`;
      // Clamp Maps-injected children so they cannot stretch page scroll.
      const kids = mapRef.current.querySelectorAll(':scope > div');
      kids.forEach((node) => {
        if (node instanceof HTMLElement) {
          node.style.maxHeight = `${h}px`;
        }
      });
      window.google.maps.event.trigger(map, 'resize');
    };
    lockCanvasSize();
    const idleListener = window.google.maps.event.addListenerOnce(map, 'idle', lockCanvasSize);
    const onWinResize = () => lockCanvasSize();
    window.addEventListener('resize', onWinResize);
    const ro =
      typeof ResizeObserver !== 'undefined' && frameEl
        ? new ResizeObserver(() => lockCanvasSize())
        : null;
    if (ro && frameEl) ro.observe(frameEl);

    if (crossingCoords.length >= 3) {
      crossingPolygonRef.current = new window.google.maps.Polygon({
        ...CROSSING_POLYGON_OPTIONS,
        paths: crossingCoords,
        map,
      });
      setHasCrossingPolygon(true);
    }

    if (polyCoords.length >= 3) {
      const polygon = new window.google.maps.Polygon({
        ...POLYGON_OPTIONS,
        paths: polyCoords,
        map,
      });
      polygonRef.current = polygon;
      setHasPolygon(true);
      const bounds = new window.google.maps.LatLngBounds();
      polyCoords.forEach((c) => bounds.extend(c));
      if (pinCenter) bounds.extend(pinCenter);
      queueCoords.forEach((c) => bounds.extend(c));
      map.fitBounds(bounds);
    }

    if (queueCoords.length >= 2) {
      queuePolylineRef.current = new window.google.maps.Polyline({
        ...queueLineOptions(),
        map,
        path: queueCoords,
      });
      setHasQueue(true);
      if (polyCoords.length < 3) {
        const bounds = new window.google.maps.LatLngBounds();
        queueCoords.forEach((c) => bounds.extend(c));
        if (pinCenter) bounds.extend(pinCenter);
        map.fitBounds(bounds);
      }
    }

    if (pinCenter) placePin(pinCenter, radiusRef.current);

    const clickListener = map.addListener('click', (event) => {
      if (!event.latLng) return;
      if (isDrawingRef.current) {
        handleDrawClickRef.current?.(event.latLng);
        return;
      }
      if (isDrawingQueueRef.current) {
        handleQueueDrawClickRef.current?.(event.latLng);
        return;
      }
      if (isPlacingPinRef.current) {
        const next = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        placePin(next, radiusRef.current);
        emit({ lat: next.lat, lng: next.lng });
        setPlacingPinMode(false);
      }
    });

    const dblClickListener = map.addListener('dblclick', (event) => {
      if (isDrawingRef.current) {
        event.stop();
        if (drawingPathRef.current.length >= 3) finishDrawingRef.current();
        return;
      }
      if (isDrawingQueueRef.current) {
        event.stop();
        if (queueDrawingPathRef.current.length >= 2) finishQueueDrawingRef.current();
      }
    });

    return () => {
      window.removeEventListener('resize', onWinResize);
      if (ro) ro.disconnect();
      if (idleListener) idleListener.remove();
      clickListener.remove();
      dblClickListener.remove();
      clearPolygonListeners();
      clearMarkerList(vertexMarkersRef);
      clearMarkerList(midpointMarkersRef);
      clearPreview();
      clearQueuePreview();
      if (polygonRef.current) polygonRef.current.setMap(null);
      if (crossingPolygonRef.current) crossingPolygonRef.current.setMap(null);
      if (queuePolylineRef.current) queuePolylineRef.current.setMap(null);
      if (markerRef.current) markerRef.current.setMap(null);
      if (circleRef.current) circleRef.current.setMap(null);
      polygonRef.current = null;
      crossingPolygonRef.current = null;
      queuePolylineRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
      mapInstanceRef.current = null;
      if (mapRef.current) mapRef.current.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    setRadius(radiusMeters || 150);
  }, [radiusMeters]);

  useEffect(() => {
    if (circleRef.current) circleRef.current.setRadius(radius);
    // Don't emit on every radius state sync — only when the user changes it
    // via InputNumber (that path calls emit directly).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  useEffect(() => {
    if (arrivalRadiusEnabled) return;
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
  }, [arrivalRadiusEnabled]);

  useEffect(() => {
    const has = lat != null && lng != null;
    setHasPin(has);
    if (!mapInstanceRef.current) return;
    if (!has) {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      return;
    }
    placePin({ lat: Number(lat), lng: Number(lng) }, radiusRef.current);
  }, [lat, lng, placePin]);

  // Keep the cyan queue polyline in sync when props change after save/hydrate.
  useEffect(() => {
    if (!loaded || !mapInstanceRef.current || isDrawingQueueRef.current) return;
    const coords = coordsFromQueuePath(queuePath);
    if (coords.length < 2) {
      if (queuePolylineRef.current) {
        queuePolylineRef.current.setMap(null);
        queuePolylineRef.current = null;
      }
      setHasQueue(false);
      return;
    }
    if (!queuePolylineRef.current) {
      queuePolylineRef.current = new window.google.maps.Polyline({
        ...queueLineOptions(),
        map: mapInstanceRef.current,
        path: coords,
      });
    } else {
      queuePolylineRef.current.setOptions(queueLineOptions());
      queuePolylineRef.current.setPath(coords);
      queuePolylineRef.current.setMap(mapInstanceRef.current);
    }
    setHasQueue(true);
  }, [loaded, queuePath]);

  useEffect(() => {
    mapInstanceRef.current?.setOptions({
      styles: hidePois ? HIDE_POI_STYLES : [],
      clickableIcons: !hidePois,
    });
  }, [hidePois]);

  if (!loaded) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: MAP_LOADING_HEIGHT }}>
        <ProgressSpinner />
      </div>
    );
  }

  const hint = (() => {
    if (isDrawing) {
      return 'Click to add points. Drag a + between points to insert one. Double-click a point to delete it, or Undo last. Click the first point or Finish to close.';
    }
    if (isDrawingQueue) {
      return 'Click along the real queue streets from the entrance toward the garita. Double-click or Finish when done (2+ points).';
    }
    if (isEditingGeofence) {
      return 'Drag white points to move them. Drag a + on an edge to add a point. Double-click a point to delete it (keep at least 3).';
    }
    if (isPlacingPin && pinEditable) {
      return hasPin
        ? 'Click the map to move the garita pin, or drag the existing pin.'
        : 'Click the map to drop the garita coordinate.';
    }
    if (pinMode === 'locked' && hasPin) {
      return 'Shared destination pin (edit above). Draw geofence + queue path for this transport/lanes.';
    }
    if (!showGeofence && !showQueue && pinEditable) {
      return 'Place the map marker for this crossing name. Routes below have their own pin, path and geofence.';
    }
    if (hasPolygon && hasQueue) {
      return 'Geofence + queue path + pin are set. Redraw or edit as needed.';
    }
    if (hasPolygon) {
      return 'Draw the queue path from the line entrance toward the garita pin.';
    }
    return 'Draw the queue geofence, then the queue path, then the garita coordinate.';
  })();

  const startGeofenceDraw = () => {
    polygonLayerRef.current = 'queue';
    drawingPathRef.current = [];
    setVertexCount(0);
    setDrawingMode(true);
  };

  const startCrossingDraw = () => {
    polygonLayerRef.current = 'crossing';
    drawingPathRef.current = [];
    setVertexCount(0);
    setDrawingMode(true);
  };

  const geofenceMenu = [
    {
      label: 'Redraw geofence',
      icon: 'pi pi-replay',
      disabled: isDrawing || isDrawingQueue || isPlacingPin,
      command: () => startGeofenceDraw(),
    },
    {
      label: 'Edit vertices',
      icon: 'pi pi-pencil',
      disabled: !hasPolygon || isDrawing,
      command: () => setEditingGeofenceMode(true),
    },
    { separator: true },
    {
      label: 'Delete geofence',
      icon: 'pi pi-trash',
      disabled: !hasPolygon && !isDrawing,
      command: () => clearGeofence(),
    },
  ];

  const queueMenu = [
    {
      label: 'Redraw queue path',
      icon: 'pi pi-replay',
      disabled: !hasQueue || isDrawing || isEditingGeofence || isPlacingPin,
      command: () => setDrawingQueueMode(true),
    },
    {
      label: 'Reverse path direction',
      icon: 'pi pi-arrows-h',
      disabled: !hasQueue || isDrawingQueue || isDrawing || isEditingGeofence || isPlacingPin,
      command: () => reverseQueuePath(),
    },
    { separator: true },
    {
      label: 'Delete queue path',
      icon: 'pi pi-trash',
      disabled: !hasQueue && !isDrawingQueue,
      command: () => clearQueue(),
    },
  ];

  const pinMenu = [
    {
      label: hasPin ? 'Move coordinate' : 'Place coordinate',
      icon: 'pi pi-map-marker',
      command: () => setPlacingPinMode(true),
    },
    { separator: true },
    {
      label: 'Delete coordinate',
      icon: 'pi pi-trash',
      disabled: !hasPin && !isPlacingPin,
      command: () => clearPin(),
    },
  ];

  const mapMenu = [
    {
      label: mapType === 'roadmap' ? 'Satellite view' : 'Map view',
      icon: 'pi pi-globe',
      command: () => {
        const next = mapType === 'roadmap' ? 'satellite' : 'roadmap';
        setMapType(next);
        mapInstanceRef.current?.setMapTypeId(next);
      },
    },
    {
      label: hidePois ? 'Show place names' : 'Hide place names',
      icon: hidePois ? 'pi pi-eye' : 'pi pi-eye-slash',
      command: () => setHidePois((v) => !v),
    },
  ];

  const busyDrawing = isDrawing || isDrawingQueue || isPlacingPin || isEditingGeofence;
  const instrReady = hasInstructions(instructions);
  const instrSteps = instructionsStepCount(instructions);

  return (
    <div>
      {instructionsEnabled && instrReady && (
        <div className="city-geo-instructions-badge mb-2">
          <i className="pi pi-check-circle" />
          <span>
            Instructions added
            {instrSteps > 0 ? ` · ${instrSteps} step${instrSteps === 1 ? '' : 's'}` : ''}
          </span>
        </div>
      )}
      <div className="flex flex-column gap-2 mb-2 city-geo-map-toolbar">
        <div className="flex gap-2 flex-wrap align-items-center">
          {showGeofence &&
            (isDrawing ? (
              <>
                <Button
                  icon="pi pi-check"
                  label={vertexCount < 3 ? `Finish (${vertexCount}/3)` : 'Finish geofence'}
                  size="small"
                  className="p-button-primary primary-button"
                  onClick={finishDrawing}
                  disabled={vertexCount < 3}
                />
                <Button
                  icon="pi pi-undo"
                  label="Undo"
                  size="small"
                  className="p-button-primary primary-button"
                  outlined
                  onClick={undoLastDrawingVertex}
                  disabled={vertexCount < 1}
                />
                <Button
                  icon="pi pi-times"
                  label="Cancel"
                  size="small"
                  severity="secondary"
                  onClick={cancelDrawing}
                />
              </>
            ) : isEditingGeofence ? (
              <Button
                icon="pi pi-check"
                label="Done editing geofence"
                size="small"
                className="p-button-primary primary-button"
                onClick={() => setEditingGeofenceMode(false)}
              />
            ) : (
              <SplitButton
                label={hasPolygon ? 'Redraw geofence' : 'Draw geofence'}
                icon="pi pi-stop"
                size="small"
                className="p-button-primary primary-button city-geo-split"
                menuClassName="city-geo-split-menu"
                model={geofenceMenu}
                onClick={startGeofenceDraw}
                disabled={busyDrawing && !isDrawing}
              />
            ))}

          {showQueue &&
            (isDrawingQueue ? (
              <>
                <Button
                  icon="pi pi-check"
                  label={queueVertexCount < 2 ? `Finish (${queueVertexCount}/2)` : 'Finish path'}
                  size="small"
                  className="p-button-primary primary-button"
                  onClick={finishQueueDrawing}
                  disabled={queueVertexCount < 2}
                />
                <Button
                  icon="pi pi-times"
                  label="Cancel"
                  size="small"
                  severity="secondary"
                  onClick={cancelQueueDrawing}
                />
              </>
            ) : (
              <SplitButton
                label={hasQueue ? 'Queue path' : 'Draw queue path'}
                icon="pi pi-directions"
                size="small"
                className="p-button-primary primary-button city-geo-split"
                menuClassName="city-geo-split-menu"
                model={queueMenu}
                onClick={() => setDrawingQueueMode(true)}
                disabled={isDrawing || isEditingGeofence || isPlacingPin}
              />
            ))}

          {pinEditable &&
            (isPlacingPin ? (
              <Button
                icon="pi pi-times"
                label="Cancel pin"
                size="small"
                severity="secondary"
                onClick={() => setPlacingPinMode(false)}
              />
            ) : (
              <SplitButton
                label={hasPin ? 'Coordinate' : 'Add coordinate'}
                icon="pi pi-map-marker"
                size="small"
                className="p-button-primary primary-button city-geo-split"
                menuClassName="city-geo-split-menu"
                model={pinMenu}
                onClick={() => setPlacingPinMode(true)}
                disabled={isDrawing || isDrawingQueue || isEditingGeofence}
              />
            ))}

          {pinMode === 'locked' && hasPin && (
            <Tag value="Shared destination" severity="info" className="align-self-center" />
          )}

          {!busyDrawing && (
            <SplitButton
              label={mapType === 'roadmap' ? 'Map' : 'Satellite'}
              icon="pi pi-map"
              size="small"
              severity="secondary"
              className="city-geo-split"
              menuClassName="city-geo-split-menu"
              model={mapMenu}
              onClick={() => {
                const next = mapType === 'roadmap' ? 'satellite' : 'roadmap';
                setMapType(next);
                mapInstanceRef.current?.setMapTypeId(next);
              }}
            />
          )}

          {instructionsEnabled && !busyDrawing && (
            <Button
              type="button"
              icon={instrReady ? 'pi pi-pencil' : 'pi pi-book'}
              label={instrReady ? 'Edit instructions' : 'Add instructions'}
              size="small"
              className="p-button-primary primary-button city-geo-instructions-btn ml-auto"
              onClick={() => setInstructionsOpen(true)}
            />
          )}
        </div>

        {crossingZoneEnabled && (
          <div className="flex align-items-center gap-2 flex-wrap city-geo-map-options">
            <label className="text-sm font-semibold white-space-nowrap">Crossing zone</label>
            <SelectButton
              value={zoneMode}
              options={[
                { label: 'Radius', value: 'radius' },
                { label: 'Geofence', value: 'geofence' },
              ]}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => {
                const next = e.value || 'radius';
                setZoneMode(next);
                emit({ crossingZoneMode: next });
              }}
              allowEmpty={false}
            />
            {zoneMode === 'geofence' && (
              <>
                <Button
                  type="button"
                  icon="pi pi-pencil"
                  label={hasCrossingPolygon ? 'Redraw crossing geofence' : 'Draw crossing geofence'}
                  size="small"
                  className="p-button-primary primary-button"
                  disabled={busyDrawing && polygonLayerRef.current !== 'crossing'}
                  onClick={startCrossingDraw}
                />
                {hasCrossingPolygon && (
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    label="Clear crossing geofence"
                    size="small"
                    severity="secondary"
                    onClick={clearCrossingGeofence}
                  />
                )}
              </>
            )}
          </div>
        )}

        {pinEditable && hasPin && arrivalRadiusEnabled && (
          <div className="flex align-items-center gap-2 flex-wrap city-geo-map-options">
            <label className="text-sm font-semibold white-space-nowrap" htmlFor="crossing-radius">
              Crossing radius (m)
            </label>
            <InputNumber
              inputId="crossing-radius"
              value={radius}
              onValueChange={(e) => {
                const next = e.value || 150;
                setRadius(next);
                emit({ crossingRadiusMeters: next });
              }}
              min={20}
              max={5000}
              showButtons
              buttonLayout="horizontal"
              decrementButtonClassName="p-button-secondary"
              incrementButtonClassName="p-button-secondary"
              inputClassName="text-center"
              inputStyle={{ width: '5.5rem' }}
              style={{ width: '13rem' }}
            />
          </div>
        )}
      </div>
      <p className="text-600 text-sm mt-0 mb-2">{hint}</p>
      <div className="city-geo-map-frame">
        <div ref={mapRef} className="city-geo-map-canvas" />
        <div
          ref={searchBoxRef}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: 'min(360px, calc(100% - 72px))',
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: '#fff',
              borderRadius: showSuggestions ? '8px 8px 0 0' : 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
              padding: '0 12px',
            }}
          >
            <i className="pi pi-search" style={{ color: '#6b7280' }} />
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="Search a city or place"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onFocus={() => {
                if (suggestions.length || searchQuery.trim().length >= 2) setShowSuggestions(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setShowSuggestions(true);
                  setHighlightIndex((prev) => Math.min(prev + 1, Math.max(suggestions.length - 1, 0)));
                  return;
                }
                if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlightIndex((prev) => Math.max(prev - 1, 0));
                  return;
                }
                if (event.key === 'Escape') {
                  setShowSuggestions(false);
                  return;
                }
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (suggestions[highlightIndex]) applyPlace(suggestions[highlightIndex]);
                }
              }}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                height: 40,
                fontSize: 14,
                background: 'transparent',
              }}
            />
            {searching ? <i className="pi pi-spin pi-spinner" style={{ color: '#6b7280' }} /> : null}
          </div>
          {showSuggestions ? (
            <div
              style={{
                background: '#fff',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
                overflow: 'hidden',
              }}
            >
              {suggestions.length ? (
                suggestions.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => applyPlace(item)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: index === highlightIndex ? '#EDE9FE' : '#fff',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <i className="pi pi-map-marker mr-2" style={{ color: '#2020A0' }} />
                    {item.label}
                  </button>
                ))
              ) : (
                <div style={{ padding: '10px 12px', fontSize: 13, color: '#6b7280' }}>
                  {searching ? 'Searching…' : 'No matching places'}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {instructionsEnabled && (
        <CityGeoInstructionsDialog
          visible={instructionsOpen}
          onHide={() => setInstructionsOpen(false)}
          value={instructions}
          routeLabel={instructionsLabel}
          onSave={(next) => emit({ instructions: next })}
        />
      )}
    </div>
  );
}
